<template>
    <div class="transitions-graph">
        <Spinner v-if="loading" />
        <div class="transitions-graph__states">
            <h1 v-html="title.join(' &rarr; ')" />
            <StateNode
                v-for="state in states"
                :key="state.key"
                ref="stateNodeRes"
                class="transitions-graph__state-node"
                :node="state"
                :actions="actions"
                :allowed-actions="allowedActions"
                @addAction="addAction"
                @updateAction="updateAction"
                @deleteAction="deleteAction"
                @updateActions="updateActions"
                @deleteRelations="deleteRelations"
            />
        </div>
        <div class="transitions-graph__footer">
            <button
                v-t="'misc.save'"
                :class="['button', 'button--big', { 'is-disabled': !canSave }]"
                :disabled="!canSave"
                @click.prevent="save"
            />
        </div>
    </div>
</template>

<script>
import { fetchAllowedActions, setGraph } from '../../api/metas';
import { makeid } from '@speechki-frontend/shared/helpers/functions';
import StateNode from './StateNode';

export default {
    name: 'TransitionsGraph',
    components: {
        StateNode,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        states: {
            type: Object,
            required: true,
        },
        actions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            allowedActions: [],
            snapshot: '',
            zoom: 1.0,
        };
    },
    computed: {
        title() {
            const { locale } = this.$i18n;
            return Object.values(this.states).map(({ label }) => label[locale]);
        },
        canSave() {
            if (!this.snapshot && !this.actions.length) return false;
            return this.snapshot !== JSON.stringify(this.actions);
        },
    },
    watch: {
        actions(value) {
            if (this.snapshot) return;
            this.snapshot = JSON.stringify(value);
        },
    },
    async created() {
        this.allowedActions = await fetchAllowedActions();
    },
    methods: {
        createAction(node = {}) {
            const id = makeid();
            const {
                action = null,
                depends_on = [],
                start = [],
                editable = true,
                created = true,
            } = node;

            return { id, action, depends_on, start, editable, created };
        },
        async addAction(node = {}) {
            const { depends_on = [] } = node;
            const createdNode = this.createAction(node);
            let newActions = this.actions.slice();
            newActions.push(createdNode);
            newActions = this.updateParentAction(newActions, createdNode.id, depends_on);

            await this.$nextTick();
            this.$emit('update:actions', newActions);
        },
        deleteAction({ id }) {
            const newActions = this.actions.filter((a) => id !== a.id);

            newActions.forEach((action) => {
                action.start = action.start.filter((a) => a !== id);
            });

            this.$emit('update:actions', newActions);
        },
        updateAction({ id, payload, overwrite = false }) {
            const newActions = this.actions.slice();
            const idx = newActions.findIndex((a) => a.id === id);
            let { start = [], depends_on = [] } = payload;

            if (!overwrite) {
                start = [...newActions[idx].start, ...start];
                depends_on = [...newActions[idx].depends_on, ...depends_on];
            }

            newActions[idx] = {
                ...newActions[idx],
                ...payload,
                start,
                depends_on,
                editable: false,
                created: false,
            };

            this.$emit('update:actions', newActions);
        },
        updateParentAction(actions, id, depends) {
            const newActions = actions.slice();
            const idx = newActions.findIndex((a) => a.id === depends.slice().pop());

            if (idx >= 0) newActions[idx].start.push(id);
            return newActions;
        },
        updateActions({ parent, node }) {
            /*  if (parent.depends_on.length) {
                console.log('parent.depends_on.length');
                return false;
            } */

            const newActions = this.actions.slice();
            const parentNodeIdx = newActions.findIndex(({ id }) => id === parent.id);

            newActions[parentNodeIdx].start = [...newActions[parentNodeIdx].start, node.id];
            this.$emit('update:actions', newActions);
        },
        deleteRelations({ id, dependId = 0, startId = 0 }) {
            const newActions = this.actions.slice();
            const idx = newActions.findIndex((a) => a.id === id);
            let { start = [], depends_on = [] } = newActions[idx];
            const payload = {};

            if (dependId) {
                payload.depends_on = depends_on.filter((aid) => aid !== dependId);
            }
            if (startId) {
                payload.start = start.filter((aid) => aid !== startId);
            }

            newActions[idx] = {
                ...newActions[idx],
                ...payload,
            };

            this.$emit('update:actions', newActions);
        },
        async save() {
            const { id } = this.$route.params;
            const formattedActions = this.actions.map(({ id, start, action, depends_on }) => ({
                id,
                start,
                action,
                depends_on,
            }));

            this.snapshot = JSON.stringify(this.actions);
            this.loading = true;

            try {
                await setGraph(id, formattedActions);
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style src="./TransitionsGraph.scss" lang="scss"></style>
